import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, Code2, Zap, Layers, Shield, Terminal, Globe } from 'lucide-react';

// Hero Component 1: Split Screen with Background Video
const HeroSplitScreen = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const codeSnippets = [
    "const innovate = () => {",
    "  const solution = await",
    "    architect.design({",
    "      scalable: true,",
    "      modern: true,",
    "      elegant: true",
    "    });",
    "  return solution.deploy();",
    "};",
    "",
    "// We craft digital excellence"
  ];

  return (
    <div className="hero-split" style={{
      minHeight: '100vh',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
      background: '#0a0a0a'
    }}>
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        onLoadedData={() => setVideoLoaded(true)}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          minWidth: '100%',
          minHeight: '100%',
          width: 'auto',
          height: 'auto',
          transform: 'translate(-50%, -50%)',
          objectFit: 'cover',
          opacity: videoLoaded ? 0.15 : 0,
          transition: 'opacity 1s ease-in',
          zIndex: 0
        }}
      >
        <source src="https://cdn.coverr.co/videos/coverr-abstract-digital-particles-flowing-6683/1080p.mp4" type="video/mp4" />
        {/* Fallback to another video URL */}
        <source src="https://player.vimeo.com/external/370467553.hd.mp4?s=e4c0e9e0c7e8d0a8f9f3f7f0e9e0c7e8&profile_id=174" type="video/mp4" />
      </video>

      {/* Dark overlay gradient */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.85) 50%, rgba(10, 10, 10, 0.95) 100%)',
        zIndex: 0
      }} />

      {/* Animated particles effect */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)
        `,
        animation: 'breathe 8s ease-in-out infinite',
        zIndex: 0
      }} />

      {/* Interactive gradient orb following mouse */}
      <div style={{
        position: 'absolute',
        width: '700px',
        height: '700px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 50%, transparent 70%)',
        left: mousePosition.x - 350,
        top: mousePosition.y - 350,
        pointerEvents: 'none',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        filter: 'blur(60px)',
        zIndex: 1
      }} />

      {/* Left Side - Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '4rem 3rem 4rem 8%',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{
          maxWidth: '650px',
          animation: 'fadeInUp 1s ease-out'
        }}>
          {/* Floating badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            background: 'rgba(59, 130, 246, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '2rem',
            padding: '0.75rem 1.5rem',
            marginBottom: '2rem',
            animation: 'slideInLeft 0.8s ease-out 0.2s backwards'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#3b82f6',
              boxShadow: '0 0 12px #3b82f6',
              animation: 'pulse 2s ease-in-out infinite'
            }} />
            <span style={{
              fontFamily: '"Manrope", sans-serif',
              fontSize: '0.875rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#3b82f6',
              fontWeight: '700'
            }}>
              Award-Winning Development Studio
            </span>
          </div>
          
          <h1 style={{
            fontFamily: '"ClashDisplay", "Manrope", sans-serif',
            fontSize: 'clamp(2.75rem, 7vw, 6rem)',
            fontWeight: '800',
            lineHeight: '1.05',
            color: '#ffffff',
            marginBottom: '1.75rem',
            letterSpacing: '-0.03em',
            animation: 'fadeInUp 1s ease-out 0.3s backwards'
          }}>
            Build The
            <br />
            <span style={{ 
              background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #c084fc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              backgroundSize: '200% auto',
              animation: 'gradientShift 3s ease-in-out infinite'
            }}>
              Impossible
            </span>
          </h1>
          
          <p style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '1.25rem',
            lineHeight: '1.8',
            color: '#cbd5e1',
            marginBottom: '3rem',
            maxWidth: '560px',
            animation: 'fadeInUp 1s ease-out 0.5s backwards'
          }}>
            We transform visionary ideas into scalable solutions. 
            <span style={{ color: '#ffffff', fontWeight: '600' }}> Precision-engineered software</span> for tomorrow's challenges.
          </p>
          
          <div style={{ 
            display: 'flex', 
            gap: '1.25rem', 
            flexWrap: 'wrap',
            animation: 'fadeInUp 1s ease-out 0.7s backwards'
          }}>
            <button style={{
              fontFamily: '"Inter", sans-serif',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '1.25rem 2.75rem',
              fontSize: '1rem',
              fontWeight: '700',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4), 0 0 0 0 rgba(59, 130, 246, 0.5)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px) scale(1.02)';
              e.target.style.boxShadow = '0 15px 40px rgba(59, 130, 246, 0.5), 0 0 0 4px rgba(59, 130, 246, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 10px 30px rgba(59, 130, 246, 0.4), 0 0 0 0 rgba(59, 130, 246, 0.5)';
            }}>
              <span>Start Your Project</span>
              <ArrowRight size={20} strokeWidth={2.5} />
            </button>
            
            <button style={{
              fontFamily: '"Inter", sans-serif',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              color: '#ffffff',
              border: '1.5px solid rgba(255, 255, 255, 0.2)',
              padding: '1.25rem 2.75rem',
              fontSize: '1rem',
              fontWeight: '600',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.background = 'rgba(59, 130, 246, 0.1)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              e.target.style.transform = 'translateY(0)';
            }}>
              Explore Work
            </button>
          </div>

          {/* Stats row */}
          <div style={{
            display: 'flex',
            gap: '3rem',
            marginTop: '4rem',
            paddingTop: '2.5rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            animation: 'fadeInUp 1s ease-out 0.9s backwards'
          }}>
            {[
              { value: '200+', label: 'Projects' },
              { value: '50+', label: 'Clients' },
              { value: '99%', label: 'Satisfaction' }
            ].map((stat, idx) => (
              <div key={idx}>
                <div style={{
                  fontFamily: '"ClashDisplay", "Manrope", sans-serif',
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '0.25rem',
                  background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  color: '#94a3b8',
                  fontWeight: '500'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Code Terminal */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 8% 4rem 3rem',
        position: 'relative',
        zIndex: 2
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)',
          backdropFilter: 'blur(30px)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '1.25rem',
          padding: '0',
          width: '100%',
          maxWidth: '550px',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(59, 130, 246, 0.1)',
          animation: 'fadeInRight 1s ease-out 0.3s backwards',
          overflow: 'hidden'
        }}>
          {/* Terminal Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1.25rem 1.5rem',
            background: 'rgba(0, 0, 0, 0.3)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                background: '#ef4444',
                boxShadow: '0 0 8px rgba(239, 68, 68, 0.5)'
              }} />
              <div style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                background: '#f59e0b',
                boxShadow: '0 0 8px rgba(245, 158, 11, 0.5)'
              }} />
              <div style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                background: '#10b981',
                boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)'
              }} />
            </div>
            <div style={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.75rem',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Terminal size={14} />
              innovate.js
            </div>
          </div>
          
          {/* Code Content */}
          <div style={{
            padding: '2rem 1.75rem',
            fontFamily: '"JetBrains Mono", "Fira Code", monospace',
            fontSize: '0.9rem',
            lineHeight: '1.8',
            color: '#e5e7eb',
            minHeight: '300px'
          }}>
            {codeSnippets.map((line, idx) => (
              <div key={idx} style={{
                animation: `typeIn 0.5s ease-out ${idx * 0.08 + 0.5}s backwards`,
                display: 'flex',
                opacity: line ? 1 : 0.3
              }}>
                <span style={{ 
                  color: '#475569', 
                  marginRight: '1.75rem', 
                  userSelect: 'none',
                  minWidth: '1.5rem',
                  textAlign: 'right'
                }}>
                  {line && String(idx + 1).padStart(2, '0')}
                </span>
                <span dangerouslySetInnerHTML={{
                  __html: line
                    .replace(/const|return|await|true/g, '<span style="color: #c084fc; font-weight: 600">$&</span>')
                    .replace(/innovate|architect|design|solution|deploy/g, '<span style="color: #60a5fa; font-weight: 600">$&</span>')
                    .replace(/scalable|modern|elegant/g, '<span style="color: #34d399">$&</span>')
                    .replace(/\/\/.+/, '<span style="color: #64748b; font-style: italic">$&</span>')
                    .replace(/[{}()]/g, '<span style="color: #fbbf24">$&</span>')
                }} />
              </div>
            ))}
            
            {/* Blinking cursor */}
            <div style={{
              display: 'inline-block',
              width: '10px',
              height: '20px',
              background: '#3b82f6',
              marginLeft: '3.25rem',
              animation: 'blink 1s step-end infinite',
              marginTop: '0.5rem'
            }} />
          </div>
          
          {/* Bottom info panel */}
          <div style={{
            padding: '1.25rem 1.75rem',
            background: 'rgba(59, 130, 246, 0.08)',
            borderTop: '1px solid rgba(59, 130, 246, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
            }}>
              <Code2 size={18} style={{ color: '#ffffff' }} />
            </div>
            <div style={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.875rem',
              color: '#cbd5e1',
              lineHeight: '1.5',
              flex: 1
            }}>
              <div style={{ fontWeight: '600', color: '#ffffff', marginBottom: '0.125rem' }}>
                Production-Ready Code
              </div>
              Building scalable solutions with modern architecture
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600;700&display=swap');
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes typeIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        @keyframes breathe {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
};

// Hero Component 2: Centered Minimalist
const HeroCentered = () => {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{
      background: '#fafafa',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle grid background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        opacity: 0.5,
        transform: `translateY(${scrollY * 0.3}px)`
      }} />

      {/* Floating accent shapes */}
      <div style={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
        top: '10%',
        right: '10%',
        filter: 'blur(60px)',
        animation: 'float 20s ease-in-out infinite'
      }} />
      
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(14, 165, 233, 0.08) 100%)',
        bottom: '15%',
        left: '15%',
        filter: 'blur(60px)',
        animation: 'float 15s ease-in-out infinite reverse'
      }} />

      <div style={{
        maxWidth: '900px',
        textAlign: 'center',
        padding: '2rem',
        position: 'relative',
        zIndex: 2
      }}>
        {/* Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '2rem',
          padding: '0.5rem 1.25rem',
          marginBottom: '2.5rem',
          fontSize: '0.875rem',
          fontFamily: '"Geist", "Inter", sans-serif',
          fontWeight: '500',
          color: '#171717',
          animation: 'fadeInDown 0.8s ease-out',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Zap size={14} style={{ color: '#0ea5e9' }} />
          Trusted by innovative companies
        </div>

        {/* Main Heading */}
        <h1 style={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: 'clamp(2.5rem, 7vw, 5.5rem)',
          fontWeight: '700',
          lineHeight: '1.1',
          color: '#171717',
          marginBottom: '1.5rem',
          letterSpacing: '-0.03em',
          animation: 'fadeInUp 0.8s ease-out 0.2s backwards'
        }}>
          Software that
          <br />
          <span style={{
            background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            scales with you
          </span>
        </h1>

        {/* Subtitle */}
        <p style={{
          fontFamily: '"Geist", "Inter", sans-serif',
          fontSize: 'clamp(1rem, 2vw, 1.25rem)',
          lineHeight: '1.7',
          color: '#525252',
          marginBottom: '3rem',
          maxWidth: '700px',
          margin: '0 auto 3rem',
          animation: 'fadeInUp 0.8s ease-out 0.4s backwards'
        }}>
          From concept to deployment, we engineer solutions that drive growth.
          <br />
          Modern architecture. Seamless integration. Built to last.
        </p>

        {/* CTA */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
          animation: 'fadeInUp 0.8s ease-out 0.6s backwards'
        }}>
          <button style={{
            fontFamily: '"Geist", "Inter", sans-serif',
            background: '#171717',
            color: '#ffffff',
            border: 'none',
            padding: '1.125rem 2.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '0.75rem',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
          }}>
            Let's Talk
          </button>
          
          <button style={{
            fontFamily: '"Geist", "Inter", sans-serif',
            background: 'transparent',
            color: '#171717',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            padding: '1.125rem 2.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '0.75rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#171717';
            e.target.style.background = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
            e.target.style.background = 'transparent';
          }}>
            Case Studies
            <ArrowRight size={18} />
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '2rem',
          marginTop: '5rem',
          animation: 'fadeInUp 0.8s ease-out 0.8s backwards'
        }}>
          {[
            { number: '150+', label: 'Projects Delivered' },
            { number: '98%', label: 'Client Satisfaction' },
            { number: '12+', label: 'Years Experience' }
          ].map((stat, idx) => (
            <div key={idx}>
              <div style={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: '2.5rem',
                fontWeight: '700',
                color: '#171717',
                marginBottom: '0.25rem'
              }}>
                {stat.number}
              </div>
              <div style={{
                fontFamily: '"Geist", "Inter", sans-serif',
                fontSize: '0.875rem',
                color: '#737373',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap');
        
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(20px, 20px);
          }
        }
      `}</style>
    </div>
  );
};

// Navigation Bar 1: Premium Floating Navbar
const NavFloating = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('services');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = ['Services', 'Work', 'About', 'Insights'];

  return (
    <>
      <nav style={{
        position: 'fixed',
        top: scrolled ? '1.5rem' : '2.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        width: scrolled ? 'min(95%, 1300px)' : 'min(90%, 1300px)',
        background: scrolled 
          ? 'rgba(255, 255, 255, 0.95)' 
          : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        borderRadius: scrolled ? '1.25rem' : '2rem',
        padding: scrolled ? '1rem 2rem' : '1.25rem 2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 1000,
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: scrolled 
          ? '0 10px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)' 
          : '0 4px 20px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.03)',
        border: scrolled ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '0.75rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s ease'
          }}>
            <Code2 size={22} style={{ color: '#ffffff' }} strokeWidth={2.5} />
          </div>
          <div style={{
            fontFamily: '"Manrope", sans-serif',
            fontSize: '1.375rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #171717 0%, #404040 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.02em'
          }}>
            DevStudio
          </div>
        </div>

        {/* Desktop Nav Links */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.03)',
          padding: '0.5rem',
          borderRadius: '0.875rem'
        }} className="desktop-nav">
          {navItems.map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              onClick={() => setActiveNav(item.toLowerCase())}
              style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.9375rem',
                fontWeight: '600',
                color: activeNav === item.toLowerCase() ? '#ffffff' : '#525252',
                textDecoration: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.625rem',
                background: activeNav === item.toLowerCase() 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
                  : 'transparent',
                boxShadow: activeNav === item.toLowerCase()
                  ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                  : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeNav !== item.toLowerCase()) {
                  e.target.style.background = 'rgba(0, 0, 0, 0.04)';
                  e.target.style.color = '#171717';
                }
              }}
              onMouseLeave={(e) => {
                if (activeNav !== item.toLowerCase()) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#525252';
                }
              }}
            >
              {item}
            </a>
          ))}
        </div>

        {/* Right side - CTA + Menu */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button style={{
            fontFamily: '"Inter", sans-serif',
            background: '#171717',
            color: '#ffffff',
            border: 'none',
            padding: '0.875rem 2rem',
            fontSize: '0.9375rem',
            fontWeight: '700',
            borderRadius: '0.75rem',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
            e.target.style.background = '#000000';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            e.target.style.background = '#171717';
          }}>
            Get Started
            <ArrowRight size={16} strokeWidth={2.5} />
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              display: 'none',
              background: 'rgba(0, 0, 0, 0.05)',
              border: 'none',
              cursor: 'pointer',
              padding: '0.75rem',
              borderRadius: '0.625rem',
              transition: 'all 0.3s ease'
            }}
            className="mobile-menu-btn"
            onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.08)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.05)'}
          >
            {mobileOpen ? <X size={22} color="#171717" /> : <Menu size={22} color="#171717" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileOpen && (
        <div style={{
          position: 'fixed',
          top: '6rem',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(90%, 400px)',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1.25rem',
          padding: '1.5rem',
          zIndex: 999,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          animation: 'slideDown 0.3s ease-out'
        }}
        className="mobile-menu">
          {navItems.map((item, idx) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              onClick={() => {
                setActiveNav(item.toLowerCase());
                setMobileOpen(false);
              }}
              style={{
                display: 'block',
                fontFamily: '"Inter", sans-serif',
                fontSize: '1rem',
                fontWeight: '600',
                color: activeNav === item.toLowerCase() ? '#3b82f6' : '#171717',
                textDecoration: 'none',
                padding: '1rem',
                borderRadius: '0.75rem',
                marginBottom: idx < navItems.length - 1 ? '0.5rem' : '0',
                background: activeNav === item.toLowerCase() ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (activeNav !== item.toLowerCase()) {
                  e.target.style.background = 'rgba(0, 0, 0, 0.04)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeNav !== item.toLowerCase()) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {item}
            </a>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 1024px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

// Navigation Bar 2: Edge-to-Edge Dark
const NavDark = () => {
  const [activeSection, setActiveSection] = useState('home');

  return (
    <nav style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      background: 'rgba(10, 10, 10, 0.95)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      padding: '1.25rem 0',
      zIndex: 1000
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 3rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Logo */}
        <div style={{
          fontFamily: '"ClashDisplay", "Manrope", sans-serif',
          fontSize: '1.5rem',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '-0.02em'
        }}>
          VERTEX
        </div>

        {/* Center Nav */}
        <div style={{
          display: 'flex',
          gap: '0.25rem',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '0.75rem',
          padding: '0.5rem'
        }} className="desktop-nav-center">
          {['Home', 'Solutions', 'Portfolio', 'Team', 'Contact'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              onClick={() => setActiveSection(item.toLowerCase())}
              style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: activeSection === item.toLowerCase() ? '#ffffff' : '#9ca3af',
                textDecoration: 'none',
                padding: '0.625rem 1.25rem',
                borderRadius: '0.5rem',
                background: activeSection === item.toLowerCase() ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== item.toLowerCase()) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.target.style.color = '#ffffff';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== item.toLowerCase()) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#9ca3af';
                }
              }}
            >
              {item}
            </a>
          ))}
        </div>

        {/* Right CTA */}
        <button style={{
          fontFamily: '"Inter", sans-serif',
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          color: '#ffffff',
          border: 'none',
          padding: '0.75rem 1.75rem',
          fontSize: '0.875rem',
          fontWeight: '600',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 6px 20px 0 rgba(59, 130, 246, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 14px 0 rgba(59, 130, 246, 0.3)';
        }}>
          Get Started
          <ArrowRight size={16} />
        </button>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .desktop-nav-center {
            display: none !important;
          }
        }
      `}</style>
    </nav>
  );
};

// Information Section 1: Feature Grid
const InfoFeatureGrid = () => {
  const features = [
    {
      icon: <Layers size={28} />,
      title: 'Scalable Architecture',
      description: 'Built on modern microservices, designed to grow with your business needs seamlessly.'
    },
    {
      icon: <Shield size={28} />,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and compliance standards protecting your critical data.'
    },
    {
      icon: <Zap size={28} />,
      title: 'Lightning Fast',
      description: 'Optimized performance delivering sub-second response times at any scale.'
    },
    {
      icon: <Globe size={28} />,
      title: 'Global Deployment',
      description: 'Multi-region infrastructure ensuring low latency for users worldwide.'
    }
  ];

  return (
    <section style={{
      background: '#ffffff',
      padding: '8rem 3rem',
      position: 'relative'
    }}>
      {/* Subtle gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '300px',
        background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.03) 0%, transparent 100%)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '5rem'
        }}>
          <div style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.875rem',
            fontWeight: '600',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#3b82f6',
            marginBottom: '1rem'
          }}>
            Why Choose Us
          </div>
          
          <h2 style={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            color: '#171717',
            marginBottom: '1.5rem',
            letterSpacing: '-0.02em'
          }}>
            Built for performance,
            <br />
            engineered for scale
          </h2>
          
          <p style={{
            fontFamily: '"Geist", "Inter", sans-serif',
            fontSize: '1.125rem',
            lineHeight: '1.7',
            color: '#525252',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            We combine cutting-edge technology with battle-tested practices to deliver solutions that stand the test of time.
          </p>
        </div>

        {/* Feature Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem'
        }}>
          {features.map((feature, idx) => (
            <div
              key={idx}
              style={{
                background: '#fafafa',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: '1rem',
                padding: '2.5rem',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                marginBottom: '1.5rem'
              }}>
                {feature.icon}
              </div>
              
              <h3 style={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#171717',
                marginBottom: '0.75rem'
              }}>
                {feature.title}
              </h3>
              
              <p style={{
                fontFamily: '"Geist", "Inter", sans-serif',
                fontSize: '0.95rem',
                lineHeight: '1.7',
                color: '#525252'
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Information Section 2: Side-by-Side with Stats
const InfoSideBySide = () => {
  return (
    <section style={{
      background: '#0a0a0a',
      padding: '8rem 3rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Gradient accents */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
        top: '-10%',
        right: '-10%',
        filter: 'blur(80px)'
      }} />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '4rem',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Left Content */}
        <div>
          <div style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.75rem',
            fontWeight: '600',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#3b82f6',
            marginBottom: '1.5rem'
          }}>
            Our Approach
          </div>
          
          <h2 style={{
            fontFamily: '"ClashDisplay", "Manrope", sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '1.5rem',
            lineHeight: '1.2',
            letterSpacing: '-0.02em'
          }}>
            Technology meets
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              craftsmanship
            </span>
          </h2>
          
          <p style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '1.125rem',
            lineHeight: '1.8',
            color: '#9ca3af',
            marginBottom: '2.5rem'
          }}>
            We don't just write code—we architect solutions. Every line is purposeful, every decision is data-driven, and every deployment is battle-tested.
          </p>

          {/* Process Steps */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            {[
              { step: '01', title: 'Discovery & Planning', desc: 'Deep dive into your requirements and goals' },
              { step: '02', title: 'Design & Architecture', desc: 'Create scalable, maintainable solutions' },
              { step: '03', title: 'Build & Iterate', desc: 'Agile development with continuous feedback' },
              { step: '04', title: 'Deploy & Support', desc: 'Smooth launch with ongoing optimization' }
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  gap: '1.5rem',
                  alignItems: 'flex-start'
                }}
              >
                <div style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#3b82f6',
                  minWidth: '40px'
                }}>
                  {item.step}
                </div>
                <div>
                  <div style={{
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: '1rem',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '0.25rem'
                  }}>
                    {item.title}
                  </div>
                  <div style={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    lineHeight: '1.6'
                  }}>
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '2rem'
        }}>
          {[
            { metric: '500K+', label: 'Lines of Code', sublabel: 'Shipped to production' },
            { metric: '24/7', label: 'Monitoring', sublabel: 'System uptime tracking' },
            { metric: '99.9%', label: 'Uptime SLA', sublabel: 'Guaranteed reliability' },
            { metric: '<50ms', label: 'API Response', sublabel: 'Average latency' }
          ].map((stat, idx) => (
            <div
              key={idx}
              style={{
                background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.8) 0%, rgba(31, 41, 55, 0.8) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '1rem',
                padding: '2rem',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
              }}
            >
              <div style={{
                fontFamily: '"ClashDisplay", "Manrope", sans-serif',
                fontSize: '2.5rem',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '0.5rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {stat.metric}
              </div>
              <div style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#e5e7eb',
                marginBottom: '0.25rem'
              }}>
                {stat.label}
              </div>
              <div style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.875rem',
                color: '#6b7280'
              }}>
                {stat.sublabel}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Widget: Carousel with Client Stories
const TestimonialsCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      quote: "The team's technical expertise and attention to detail transformed our legacy system into a modern, scalable platform. Deployment was seamless.",
      author: "Sarah Chen",
      role: "CTO, FinTech Innovations",
      company: "FinTech Inc.",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop"
    },
    {
      quote: "Working with them felt like having an extended team. They understood our vision and delivered beyond expectations. Our user engagement tripled.",
      author: "Marcus Rodriguez",
      role: "Head of Product, HealthStream",
      company: "HealthStream",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop"
    },
    {
      quote: "Exceptional code quality and architecture. Six months post-launch, and we haven't encountered a single critical bug. That's unprecedented for us.",
      author: "Emily Watson",
      role: "VP Engineering, DataFlow",
      company: "DataFlow Systems",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section style={{
      background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
      padding: '8rem 3rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        border: '2px solid rgba(59, 130, 246, 0.1)',
        animation: 'float 8s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        right: '8%',
        width: '150px',
        height: '150px',
        borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
        animation: 'float 10s ease-in-out infinite reverse'
      }} />

      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '4rem'
        }}>
          <div style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.875rem',
            fontWeight: '600',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#3b82f6',
            marginBottom: '1rem'
          }}>
            Client Success Stories
          </div>
          
          <h2 style={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '700',
            color: '#171717',
            letterSpacing: '-0.02em'
          }}>
            Trusted by industry leaders
          </h2>
        </div>

        {/* Testimonial Cards */}
        <div style={{
          position: 'relative',
          minHeight: '400px'
        }}>
          {testimonials.map((testimonial, idx) => (
            <div
              key={idx}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                opacity: idx === activeIndex ? 1 : 0,
                transform: idx === activeIndex ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: idx === activeIndex ? 'auto' : 'none'
              }}
            >
              <div style={{
                background: '#ffffff',
                borderRadius: '1.5rem',
                padding: '3rem',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}>
                {/* Quote Icon */}
                <div style={{
                  fontSize: '4rem',
                  lineHeight: '1',
                  color: '#3b82f6',
                  opacity: 0.2,
                  marginBottom: '1rem',
                  fontFamily: 'Georgia, serif'
                }}>
                  "
                </div>

                {/* Quote Text */}
                <p style={{
                  fontFamily: '"Crimson Pro", Georgia, serif',
                  fontSize: '1.5rem',
                  lineHeight: '1.6',
                  color: '#171717',
                  marginBottom: '2.5rem',
                  fontStyle: 'italic'
                }}>
                  {testimonial.quote}
                </p>

                {/* Author Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.25rem'
                }}>
                  <img
                    src={testimonial.image}
                    alt={testimonial.author}
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid #f0f9ff'
                    }}
                  />
                  <div>
                    <div style={{
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: '1.125rem',
                      fontWeight: '700',
                      color: '#171717',
                      marginBottom: '0.25rem'
                    }}>
                      {testimonial.author}
                    </div>
                    <div style={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.9rem',
                      color: '#525252'
                    }}>
                      {testimonial.role}
                    </div>
                    <div style={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.875rem',
                      color: '#737373',
                      marginTop: '0.125rem'
                    }}>
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.75rem',
          marginTop: '3rem'
        }}>
          {testimonials.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              style={{
                width: idx === activeIndex ? '40px' : '12px',
                height: '12px',
                borderRadius: '6px',
                background: idx === activeIndex ? '#3b82f6' : '#d1d5db',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              aria-label={`Go to testimonial ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&display=swap');
      `}</style>
    </section>
  );
};

// Tech Stack Widget: Animated Technology Showcase
const TechStackShowcase = () => {
  const [hoveredTech, setHoveredTech] = useState(null);

  const techCategories = [
    {
      category: "Frontend",
      color: "#3b82f6",
      technologies: [
        { name: "React", proficiency: 95 },
        { name: "Next.js", proficiency: 90 },
        { name: "TypeScript", proficiency: 92 },
        { name: "Tailwind", proficiency: 88 }
      ]
    },
    {
      category: "Backend",
      color: "#8b5cf6",
      technologies: [
        { name: "Node.js", proficiency: 93 },
        { name: "Python", proficiency: 90 },
        { name: "PostgreSQL", proficiency: 87 },
        { name: "Redis", proficiency: 85 }
      ]
    },
    {
      category: "DevOps",
      color: "#0ea5e9",
      technologies: [
        { name: "AWS", proficiency: 91 },
        { name: "Docker", proficiency: 89 },
        { name: "Kubernetes", proficiency: 86 },
        { name: "GitHub Actions", proficiency: 92 }
      ]
    },
    {
      category: "Mobile",
      color: "#6366f1",
      technologies: [
        { name: "React Native", proficiency: 88 },
        { name: "Flutter", proficiency: 82 },
        { name: "Swift", proficiency: 80 },
        { name: "Kotlin", proficiency: 79 }
      ]
    }
  ];

  return (
    <section style={{
      background: '#0f0f0f',
      padding: '8rem 3rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated grid background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        opacity: 0.5,
        animation: 'gridScroll 20s linear infinite'
      }} />

      {/* Gradient orbs */}
      <div style={{
        position: 'absolute',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
        top: '-20%',
        left: '-10%',
        filter: 'blur(100px)',
        animation: 'pulse 8s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
        bottom: '-15%',
        right: '-5%',
        filter: 'blur(100px)',
        animation: 'pulse 10s ease-in-out infinite reverse'
      }} />

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '5rem'
        }}>
          <div style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.75rem',
            fontWeight: '600',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#3b82f6',
            marginBottom: '1.5rem'
          }}>
            Our Technology Arsenal
          </div>
          
          <h2 style={{
            fontFamily: '"ClashDisplay", "Manrope", sans-serif',
            fontSize: 'clamp(2rem, 5vw, 4rem)',
            fontWeight: '700',
            color: '#ffffff',
            marginBottom: '1rem',
            letterSpacing: '-0.02em'
          }}>
            Powered by cutting-edge
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              technology
            </span>
          </h2>
          
          <p style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '1.125rem',
            color: '#9ca3af',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            We leverage industry-leading tools and frameworks to build robust, scalable solutions
          </p>
        </div>

        {/* Tech Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {techCategories.map((category, catIdx) => (
            <div
              key={catIdx}
              style={{
                background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.6) 0%, rgba(31, 41, 55, 0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '1.25rem',
                padding: '2rem',
                transition: 'all 0.4s ease',
                animation: `fadeInUp 0.6s ease-out ${catIdx * 0.1}s backwards`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = `${category.color}40`;
                e.currentTarget.style.boxShadow = `0 20px 40px ${category.color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Category Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: category.color,
                  boxShadow: `0 0 12px ${category.color}`
                }} />
                <h3 style={{
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#ffffff',
                  margin: 0
                }}>
                  {category.category}
                </h3>
              </div>

              {/* Technologies */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem'
              }}>
                {category.technologies.map((tech, techIdx) => (
                  <div
                    key={techIdx}
                    onMouseEnter={() => setHoveredTech(`${catIdx}-${techIdx}`)}
                    onMouseLeave={() => setHoveredTech(null)}
                  >
                    {/* Tech Name */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: hoveredTech === `${catIdx}-${techIdx}` ? '#ffffff' : '#d1d5db',
                        transition: 'color 0.3s ease'
                      }}>
                        {tech.name}
                      </span>
                      <span style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.875rem',
                        color: category.color,
                        fontWeight: '600'
                      }}>
                        {tech.proficiency}%
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div style={{
                      height: '6px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        height: '100%',
                        width: hoveredTech === `${catIdx}-${techIdx}` ? `${tech.proficiency}%` : '0%',
                        background: `linear-gradient(90deg, ${category.color} 0%, ${category.color}CC 100%)`,
                        borderRadius: '3px',
                        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 0 12px ${category.color}60`
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '5rem'
        }}>
          <p style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '1rem',
            color: '#9ca3af',
            marginBottom: '1.5rem'
          }}>
            Ready to build something amazing together?
          </p>
          <button style={{
            fontFamily: '"Inter", sans-serif',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '1rem 2.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            borderRadius: '0.75rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-3px)';
            e.target.style.boxShadow = '0 8px 30px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.3)';
          }}>
            Discuss Your Project
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes gridScroll {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(60px);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
      `}</style>
    </section>
  );
};

// Main Demo Component
export default function SoftwareDevComponents() {
  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Navigation Examples */}
      <NavFloating />
      {/* <NavDark /> - Use one nav at a time */}
      
      {/* Hero Examples */}
      <HeroSplitScreen />
      {/* <HeroCentered /> - Show one hero per page */}
      
      {/* Information Sections */}
      <InfoFeatureGrid />
      <InfoSideBySide />
      
      {/* New Widgets */}
      <TestimonialsCarousel />
      <TechStackShowcase />
    </div>
  );
}